/**
 * Swatches for the Colors step's "From your logo" row: the dominant colours
 * in an uploaded logo image, quantized so JPEG noise and anti-aliasing don't
 * turn one visual colour into a dozen near-duplicate hexes.
 *
 * `quantizeSwatches` is PURE — it takes raw RGBA bytes, not an image — so the
 * binning/ordering/drop contract is unit-tested in node; `swatchesFromImage`
 * needs a real `<canvas>` to decode the image and is exercised only in the
 * browser.
 */

/** 256 / 16 = 16 levels per channel — coarse enough that anti-aliased edges
 *  land in the same bin as the flat colour they're feathering into. */
const CHANNEL_BIN = 16;
/** Below this, a pixel is transparent enough to not count as logo colour. */
const ALPHA_MIN = 128;
/** A bin whose averaged channels all fall in these bands reads as the logo's
 *  background/outline (near-black or near-white), not its brand colour —
 *  dropped unless it's dominant enough that it evidently IS the brand colour.
 *  Inferred thresholds — the brief specifies the 16-level channel binning and
 *  the 40% share exception, but not where "near" starts; 24/232 is roughly
 *  the outer 10% of the 0-255 range on each end. No spec anchor, so revisit
 *  if a real logo's edge colour turns out to be mis-classified. */
const NEAR_BLACK_MAX = 24;
const NEAR_WHITE_MIN = 232;
/** "From your logo" shows at most 4 swatches (Colors step). */
const DEFAULT_MAX_SWATCHES = 4;
const DEFAULT_MIN_SHARE = 0.4;

interface Bin {
  count: number;
  rSum: number;
  gSum: number;
  bSum: number;
}

/**
 * Bins pixels `CHANNEL_BIN` levels per channel (so near-identical colours
 * collapse into one swatch) and reports each bin's ACTUAL average colour, not
 * the bin's nominal centre — a uniform block of pixels therefore round-trips
 * to its exact input hex; only genuinely mixed pixels drift toward a blend.
 *
 * Skips pixels whose alpha is below `ALPHA_MIN`. Drops near-black/near-white
 * bins unless they hold at least `minShare` of the counted pixels (a white
 * background must not out-rank the logo's own colour, but a logo that IS
 * mostly black or white should still report it). Returns ≤ `max` hex strings,
 * ordered by share descending.
 */
export function quantizeSwatches(
  rgba: Uint8ClampedArray,
  opts: { max?: number; minShare?: number } = {},
): string[] {
  const max = opts.max ?? DEFAULT_MAX_SWATCHES;
  const minShare = opts.minShare ?? DEFAULT_MIN_SHARE;

  const bins = new Map<string, Bin>();
  let total = 0;
  for (let i = 0; i + 3 < rgba.length; i += 4) {
    if (rgba[i + 3] < ALPHA_MIN) continue;
    const r = rgba[i];
    const g = rgba[i + 1];
    const b = rgba[i + 2];
    const key = `${binOf(r)},${binOf(g)},${binOf(b)}`;
    const bin = bins.get(key);
    if (bin) {
      bin.count++;
      bin.rSum += r;
      bin.gSum += g;
      bin.bSum += b;
    } else {
      bins.set(key, { count: 1, rSum: r, gSum: g, bSum: b });
    }
    total++;
  }
  if (total === 0) return [];

  return Array.from(bins.values())
    .map((bin) => ({
      r: Math.round(bin.rSum / bin.count),
      g: Math.round(bin.gSum / bin.count),
      b: Math.round(bin.bSum / bin.count),
      share: bin.count / total,
    }))
    .filter((s) => (isNearBlack(s) || isNearWhite(s) ? s.share >= minShare : true))
    .sort((x, y) => y.share - x.share)
    .slice(0, max)
    .map((s) => toHex(s.r, s.g, s.b));
}

function binOf(channel: number): number {
  return Math.floor(channel / CHANNEL_BIN);
}

function isNearBlack(c: { r: number; g: number; b: number }): boolean {
  return c.r <= NEAR_BLACK_MAX && c.g <= NEAR_BLACK_MAX && c.b <= NEAR_BLACK_MAX;
}

function isNearWhite(c: { r: number; g: number; b: number }): boolean {
  return c.r >= NEAR_WHITE_MIN && c.g >= NEAR_WHITE_MIN && c.b >= NEAR_WHITE_MIN;
}

function toHex(r: number, g: number, b: number): string {
  const byte = (v: number): string => Math.max(0, Math.min(255, v)).toString(16).padStart(2, "0");
  return `#${byte(r)}${byte(g)}${byte(b)}`.toUpperCase();
}

/**
 * Decodes `dataUrl` onto a 64×64 canvas — a logo's dominant colours don't
 * need full resolution, and 64² keeps `quantizeSwatches`'s pixel loop
 * instant — then quantizes it. Browser-only: decoding an image needs a real
 * `<canvas>`.
 */
export async function swatchesFromImage(dataUrl: string): Promise<string[]> {
  if (typeof document === "undefined") {
    throw new Error("swatchesFromImage requires a browser (no `document`)");
  }
  const SIZE = 64;
  const img = await loadImage(dataUrl);
  const canvas = document.createElement("canvas");
  canvas.width = SIZE;
  canvas.height = SIZE;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("swatchesFromImage: 2D canvas context unavailable");
  ctx.clearRect(0, 0, SIZE, SIZE);
  ctx.drawImage(img, 0, 0, SIZE, SIZE);
  const { data } = ctx.getImageData(0, 0, SIZE, SIZE);
  return quantizeSwatches(data);
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("swatchesFromImage: could not decode the logo image"));
    img.src = src;
  });
}
